import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/* ── tiny helper ── */
const API = {
  toString: () => window.API_URL || 'http://localhost:8000'
};

const initialForm = {
  name: '',
  profession: '',
  skill: '',
  description: '',
  linkedin: '',
  twitter: '',
};

const ManageTeam = () => {
  const [team, setTeam]           = useState([]);
  const [form, setForm]           = useState(initialForm);
  const [image, setImage]         = useState(null);
  const [preview, setPreview]     = useState(null);
  const [editId, setEditId]       = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast]         = useState(null);   // { msg, type }
  const fileRef                   = useRef(null);

  /* ── fetch ── */
  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/team`);
      if (Array.isArray(res.data)) {
        setTeam(res.data);
      } else {
        console.error("Expected array for team, but got:", res.data);
        setTeam([]);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchTeam(); }, []);

  /* ── toast helper ── */
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  /* ── image pick ── */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  /* ── form field ── */
  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken');
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (image) fd.append('image', image);

    try {
      if (editId) {
        await axios.put(`${API}/team/${editId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        });
        showToast('Team member updated successfully!');
      } else {
        await axios.post(`${API}/team`, fd, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        });
        showToast('Team member added successfully!');
      }
      fetchTeam();
      resetForm();
    } catch (err) {
      console.error(err);
      showToast(`Error ${editId ? 'updating' : 'adding'} team member`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── reset ── */
  const resetForm = () => {
    setForm(initialForm);
    setImage(null);
    setPreview(null);
    setEditId(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  /* ── edit ── */
  const handleEdit = (member) => {
    setEditId(member._id);
    setForm({
      name:        member.name        || '',
      profession:  member.profession  || '',
      skill:       member.skill       || '',
      description: member.description || '',
      linkedin:    member.linkedin    || '',
      twitter:     member.twitter     || '',
    });
    setImage(null);
    setPreview(`${API}/uploads/${member.image}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    const token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/team/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTeam();
      showToast('Team member removed.');
    } catch (err) {
      console.error(err);
      showToast('Error deleting member', 'error');
    }
  };

  /* ────────────── RENDER ────────────── */
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", position: 'relative' }}>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'success'
            ? 'linear-gradient(135deg,#16a34a,#15803d)'
            : 'linear-gradient(135deg,#dc2626,#b91c1c)',
          color: '#fff', padding: '14px 22px', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
          animation: 'slideIn .3s ease',
        }}>
          <span>{toast.type === 'success' ? '✓' : '✕'}</span>
          {toast.msg}
        </div>
      )}

      {/* ── Page title ── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: 0 }}>
          Team Management
        </h1>
        <p style={{ color: '#6b7280', marginTop: 4, fontSize: 14 }}>
          Add, edit, or remove members shown on the website's Team section.
        </p>
      </div>

      {/* ══════════════════════════════
          ADD / EDIT FORM
      ══════════════════════════════ */}
      <div style={{
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        border: '1px solid #f3f4f6',
        padding: '32px 36px',
        marginBottom: 36,
      }}>
        {/* Form header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: 'linear-gradient(135deg,#f06600,#ff9a4a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
            }}>
              {editId ? '✎' : '+'}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
                {editId ? 'Edit Team Member' : 'Add New Member'}
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
                {editId ? 'Update the fields below and save.' : 'Fill in all required fields to publish.'}
              </p>
            </div>
          </div>
          {editId && (
            <button onClick={resetForm} style={{
              background: '#f3f4f6', border: 'none', borderRadius: 8,
              padding: '8px 16px', cursor: 'pointer', fontSize: 13,
              fontWeight: 600, color: '#374151',
            }}>
              ✕ Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>

          {/* Row 1: Image upload + preview */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 24, alignItems: 'flex-start' }}>

            {/* Image box */}
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                width: 120, height: 140, flexShrink: 0,
                borderRadius: 16, border: '2px dashed #e5e7eb',
                background: '#fafafa', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', overflow: 'hidden',
                transition: 'border-color .2s',
                position: 'relative',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#f06600'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              {preview ? (
                <>
                  <img src={preview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                  <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity .2s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                  >
                    <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>Change</span>
                  </div>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 28, marginBottom: 6 }}>📷</span>
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textAlign: 'center', lineHeight: 1.4 }}>
                    Click to<br />Upload Photo
                  </span>
                </>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
              required={!editId}
            />

            {/* Right fields: name + profession */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FormField
                label="Full Name *"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Sarah Ahmed"
                required
              />
              <FormField
                label="Job Title / Profession *"
                name="profession"
                value={form.profession}
                onChange={handleChange}
                placeholder="e.g. Senior UI/UX Designer"
                required
              />
            </div>
          </div>

          {/* Row 2: Skills */}
          <FormField
            label="Skills / Tags *"
            name="skill"
            value={form.skill}
            onChange={handleChange}
            placeholder="e.g. Figma · React · Tailwind · Node.js  (separate with ·)"
            required
            hint="Separate each skill with a · dot. These appear as tags on the card."
          />

          {/* Row 3: Description */}
          <FormField
            label="Bio / Description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="A short bio that gives visitors a glimpse into this person's background, expertise, and personality..."
            multiline
            hint="This text will be partially shown on the card with a fade effect — keep it 2–3 sentences."
          />

          {/* Row 4: Social links */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 8 }}>
            <FormField
              label="LinkedIn URL"
              name="linkedin"
              value={form.linkedin}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/username"
              icon="💼"
            />
            <FormField
              label="Twitter / X URL"
              name="twitter"
              value={form.twitter}
              onChange={handleChange}
              placeholder="https://twitter.com/username"
              icon="𝕏"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              marginTop: 24,
              width: '100%',
              padding: '14px 0',
              borderRadius: 12,
              border: 'none',
              background: isSubmitting
                ? '#d1d5db'
                : 'linear-gradient(135deg,#f06600 0%,#ff8c38 100%)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 15,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              letterSpacing: 0.3,
              boxShadow: isSubmitting ? 'none' : '0 6px 20px rgba(240,102,0,0.35)',
              transition: 'all .25s',
            }}
          >
            {isSubmitting
              ? (editId ? 'Saving Changes…' : 'Adding Member…')
              : (editId ? '✓ Save Changes' : '+ Publish Team Member')}
          </button>
        </form>
      </div>

      {/* ══════════════════════════════
          TEAM LIST
      ══════════════════════════════ */}
      <div style={{
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        border: '1px solid #f3f4f6',
        padding: '28px 36px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
            Current Team Members
            <span style={{
              marginLeft: 10, background: '#fef3e8', color: '#f06600',
              fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
            }}>
              {team.length}
            </span>
          </h2>
        </div>

        {!Array.isArray(team) || team.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
            <p style={{ fontWeight: 600, margin: 0 }}>No team members yet.</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>Use the form above to add the first one.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
            {Array.isArray(team) && team.map(member => (
              <MemberCard
                key={member._id}
                member={member}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════
   REUSABLE FIELD COMPONENT
══════════════════════════════ */
const FormField = ({ label, name, value, onChange, placeholder, required, multiline, hint, icon }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, letterSpacing: 0.3 }}>
      {label}
    </label>
    {multiline ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={4}
        style={fieldStyle}
      />
    ) : (
      <div style={{ position: 'relative' }}>
        {icon && (
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, pointerEvents: 'none' }}>
            {icon}
          </span>
        )}
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          style={{ ...fieldStyle, paddingLeft: icon ? 36 : 14 }}
        />
      </div>
    )}
    {hint && <p style={{ margin: '5px 0 0', fontSize: 11, color: '#9ca3af' }}>{hint}</p>}
  </div>
);

const fieldStyle = {
  width: '100%',
  padding: '11px 14px',
  border: '1.5px solid #e5e7eb',
  borderRadius: 10,
  fontSize: 14,
  color: '#111827',
  background: '#fafafa',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  transition: 'border-color .2s',
  resize: 'vertical',
};

/* ══════════════════════════════
   MEMBER CARD (admin list view)
══════════════════════════════ */
const MemberCard = ({ member, onEdit, onDelete }) => {
  const tags = (member.skill || '')
    .split(/[·,|]/)
    .map(s => s.trim())
    .filter(Boolean)
    .slice(0, 3);

  return (
    <div style={{
      border: '1.5px solid #f3f4f6',
      borderRadius: 16,
      overflow: 'hidden',
      background: '#fafafa',
      transition: 'box-shadow .2s, transform .2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(240,102,0,0.12)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Photo banner */}
      <div style={{ height: 90, background: 'linear-gradient(135deg,#f06600,#ffb380)', position: 'relative', overflow: 'hidden' }}>
        <img
          src={`${window.API_URL}/uploads/${member.image}`}
          alt={member.name}
          style={{
            position: 'absolute', bottom: 0, right: 0,
            height: '130%', width: 'auto', objectFit: 'cover', objectPosition: 'top',
            opacity: 0.35,
          }}
        />
        {/* Avatar */}
        <div style={{
          position: 'absolute', bottom: -28, left: 18,
          width: 56, height: 56, borderRadius: '50%',
          border: '3px solid #fff', overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
        }}>
          <img
            src={`${window.API_URL}/uploads/${member.image}`}
            alt={member.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '36px 18px 18px' }}>
        <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: '#111827' }}>{member.name}</p>
        <p style={{ margin: '2px 0 8px', fontSize: 12, color: '#f06600', fontWeight: 700, letterSpacing: 0.5 }}>
          {member.profession}
        </p>

        {/* Tags */}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
            {tags.map((t, i) => (
              <span key={i} style={{
                background: '#fef3e8', border: '1px solid #fcd9b5',
                color: '#c2410c', fontSize: 10, fontWeight: 700,
                padding: '2px 8px', borderRadius: 6,
              }}>{t}</span>
            ))}
          </div>
        )}

        {/* Description preview */}
        {member.description && (
          <p style={{
            margin: '0 0 12px', fontSize: 12, color: '#6b7280',
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {member.description}
          </p>
        )}

        {/* Socials */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {member.linkedin && (
            <a href={member.linkedin} target="_blank" rel="noreferrer"
              style={{ fontSize: 11, color: '#0a66c2', fontWeight: 600, textDecoration: 'none' }}>
              💼 LinkedIn
            </a>
          )}
          {member.twitter && (
            <a href={member.twitter} target="_blank" rel="noreferrer"
              style={{ fontSize: 11, color: '#374151', fontWeight: 600, textDecoration: 'none' }}>
              𝕏 Twitter
            </a>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => onEdit(member)}
            style={{
              flex: 1, padding: '9px 0', borderRadius: 9, border: '1.5px solid #e5e7eb',
              background: '#fff', color: '#374151', fontWeight: 700, fontSize: 13,
              cursor: 'pointer', transition: 'all .2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#f06600'; e.currentTarget.style.color = '#f06600'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
          >
            ✎ Edit
          </button>
          <button
            onClick={() => onDelete(member._id)}
            style={{
              flex: 1, padding: '9px 0', borderRadius: 9, border: '1.5px solid #fee2e2',
              background: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: 13,
              cursor: 'pointer', transition: 'all .2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
          >
            🗑 Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageTeam;
